import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

import LazyHydratedGooglePublisherTagLandscapeAdSlot from 'components/AdSlots/GooglePublisherTagLandscapeAdSlot/LazyHydratedGooglePublisherTagLandscapeAdSlot';
import FlatLayout from 'components/Layout/FlatLayout';

import useExclusiveOffer from 'hooks/useExclusiveOffer';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import useSendPageView from 'hooks/useSendPageView';

import { sendEvent } from 'lib/gtag';
import { getOfferPageEventCategory } from 'lib/offer';
import { offerQueryKeys } from 'lib/queryKeys';
import { sendSupixEvent } from 'lib/supix';

import { useUser } from 'providers/UserProvider';
import { useUtm } from 'providers/UtmProvider';

import { getOfferInfo } from 'services/offer';

import Breadcrumb from './Breadcrumb';
import Head from './Head';
import InsiderData from './InsiderData';
import Main from './Main';
import OfferTabs from './OfferTabs';
import RelatedCategories from './RelatedCategories';
import RelatedOffers from './RelatedOffers';
import Schema from './Schema';
import Sidebar from './Sidebar';
import WishlistSuggestions from './WishlistSuggestions';

import LINKS from 'constants/links';
import OFFER from 'constants/offer';
import STATUS_CODES from 'constants/statusCodes';

const Loading = dynamic(() => import('./Loading'), { ssr: false });

const PriceHistory = dynamic(() => import('./PriceHistory'));

const PriceComparator = dynamic(() => import('./PriceComparator'));

const WaitingBeApprovedAlert = dynamic(
  () => import('./WaitingBeApprovedAlert'),
  { ssr: false }
);

const CommentsList = dynamic(() => import('./CommentsList'));

const OfferPage = ({
  featuredStores,
  isIndexActive = true,
  isPromobitHasTheBestPrice = false,
  offerKey,
  offersToCompare = [],
  relatedOffers,
  reviews = [],
  serverComments = [],
  serverOffer,
  sidebarAds = [],
}) => {
  const router = useRouter();
  const { isUtmLoaded } = useUtm();
  const { loadingLoggedInUser } = useUser();
  const { isOfferBlocked, onUnloggedUserTriesOpenExclusiveOffer } =
    useExclusiveOffer();
  const priceComparatorRef = useRef(null);

  // Most offers comes directly from server for SEO purposes.
  // There are edge cases though where some offers with certain statuses
  // may need the authentication token to be fetched. These needs to be made
  // on client, after the current user is fully loaded.
  // In these cases we don't care about SEO
  const { data: clientOffer } = useQuery({
    queryKey: offerQueryKeys.offer({ offerKey }),
    queryFn: ({ signal }) => getOfferInfo({ offerId: offerKey }, { signal }),
    enabled: !serverOffer && !loadingLoggedInUser,
    // Currently the only status specific offer that the user should be able
    // to access is `WAITING`, and only if the user that posted the offer is
    // the same as the user who is accessing it, otherwise we redirect
    onError: ({ message }) => {
      // For some reason someone tried to access an `WAITING` offer and is
      // also not logged in
      if (message === STATUS_CODES[401]) {
        router.push(LINKS.HOME);
      }
    },
  });

  const offer = serverOffer || clientOffer;
  const isOfferFinished = offer?.offerStatusName === OFFER.STATUSES.FINISHED;
  const isShowingPriceComparator = offersToCompare.length > 0;
  const isShowingPriceHistory = Boolean(
    offer?.referencePrices &&
    offer?.offerStatusName !== OFFER.STATUSES.FINISHED &&
    !isShowingPriceComparator
  );
  const gaEventCategory = getOfferPageEventCategory(offer?.offerStatusName);

  useIntersectionObserver({
    target: priceComparatorRef,
    threshold: 0.6,
    enabled: isShowingPriceComparator,
    onIntersect: () => {
      sendEvent({
        action: 'view_offer_compare_section',
        category: gaEventCategory,
        label: isPromobitHasTheBestPrice
          ? 'compare_best_price'
          : 'compare_not_best_price',
      });
    },
  });

  useEffect(() => {
    if (offer && offersToCompare.length > 0) {
      sendEvent({
        action: 'view_offer_compare',
        category: gaEventCategory,
        label: isPromobitHasTheBestPrice
          ? 'compare_best_price'
          : 'compare_not_best_price',
      });
    }
  }, [offersToCompare, offer]);

  useEffect(() => {
    if (offer && isUtmLoaded) {
      sendSupixEvent('/ofr', {
        oid: offer.offerId,
        status: offer.offerStatus,
        category: offer.categoryId,
      });
    }
  }, [offer, isUtmLoaded, router.asPath]);

  useEffect(() => {
    if (offer && isOfferBlocked(offer.offerUserVisibility, offer.offerSlug)) {
      onUnloggedUserTriesOpenExclusiveOffer(
        offer.offerSlug,
        offer.offerUserVisibility
      );
    }
  }, [isOfferBlocked, onUnloggedUserTriesOpenExclusiveOffer]);

  useSendPageView(offer?.offerId, 'offer', {
    enabled: !!offer?.offerId,
  });

  if (!offer) {
    return <Loading />;
  }

  return (
    <>
      <FlatLayout.Margin>
        <FlatLayout.Heading>
          <Breadcrumb {...offer} />
          <Head
            isIndexActive={isIndexActive}
            offerKey={offerKey}
            reviewsCount={reviews.length}
            {...offer}
          />
          {/* Only renders schemas when offer comes directly from server, when
            this don't occur, we should not have schemas */}
          {serverOffer && (
            <Schema
              {...serverOffer}
              offersToCompare={offersToCompare}
              reviewRating={serverOffer.reviewRate}
              reviews={reviews}
            />
          )}
          <WaitingBeApprovedAlert
            offerStatusName={offer.offerStatusName}
            userId={offer.userId}
          />
        </FlatLayout.Heading>
        <FlatLayout.Body className="gap-8 lg:gap-0">
          <Main
            gaEventCategory={gaEventCategory}
            isFinished={isOfferFinished}
            isPromobitHasTheBestPrice={isPromobitHasTheBestPrice}
            isShowingPriceComparator={isShowingPriceComparator}
            isShowingPriceHistory={isShowingPriceHistory}
            keywords={offer.offerKeywords}
            offerTags={offer.offerTags}
            offersToCompare={offersToCompare}
            serverComments={serverComments}
            {...offer}
          />
          {isShowingPriceHistory && (
            <PriceHistory
              gaEventCategory={gaEventCategory}
              offerPrice={offer.offerPrice}
              offerReferencePrices={offer.referencePrices}
            />
          )}
          <section className="flex flex-row lg:mt-10 lg:w-[624px] lg:gap-10 xl:w-unset">
            <div className="flex w-full flex-col">
              {isShowingPriceComparator && (
                <div ref={priceComparatorRef} id="price-comparator">
                  <PriceComparator
                    categoryName={offer.categoryName}
                    gaEventCategory={gaEventCategory}
                    isPromobitHasTheBestPrice={isPromobitHasTheBestPrice}
                    offerCoupon={offer.offerCoupon}
                    offerCta={offer.offerCta}
                    offerId={offer.offerId}
                    offerPrice={offer.offerPrice}
                    offerSlug={offer.offerSlug}
                    offerUserVisibility={offer.offerUserVisibility}
                    offersToCompare={offersToCompare}
                    subcategoryName={offer.subcategoryName}
                  />
                </div>
              )}
              <OfferTabs gaEventCategory={gaEventCategory} offer={offer} />
              <CommentsList
                aliasId={offer.aliasId}
                className="mb-8 lg:hidden"
                gaEventCategory={gaEventCategory}
                isOfferCommentsAllowed={offer.isOfferCommentsAllowed}
                isShowingPriceComparator={offer.isShowingPriceComparator}
                offerCommentsCount={offer.offerCommentsCount}
                offerId={offer.offerId}
                offerStatusName={offer.offerStatusName}
                serverComments={offer.serverComments}
              />

              <WishlistSuggestions
                gaEventCategory={gaEventCategory}
                keywords={offer.offerKeywords}
              />
              <LazyHydratedGooglePublisherTagLandscapeAdSlot
                adId="offer_page_main_content_1_320x50_468x60_728x90"
                adPath="/promobit.com.br_offer_page/main_content_1_320x50_468x60_728x90"
                className="min-h-12 w-full md:min-h-16 lg:mb-10 lg:max-w-[768px]"
              />
            </div>
            <Sidebar
              featuredStores={featuredStores}
              gaEventCategory={gaEventCategory}
              sidebarAds={sidebarAds}
            />
          </section>
          <section className="flex flex-col gap-8 lg:gap-14">
            {relatedOffers && relatedOffers.length > 0 && (
              <RelatedOffers
                categorySlug={offer.categorySlug}
                gaEventCategory={gaEventCategory}
                relatedOffers={relatedOffers}
                subcategorySlug={offer.subcategorySlug}
              />
            )}
            <RelatedCategories
              categories={offer.relatedSubcategories}
              gaEventCategory={gaEventCategory}
              storeName={offer.storeName}
              storeParam={offer.storeParam}
            />
          </section>
        </FlatLayout.Body>
      </FlatLayout.Margin>
      <FlatLayout.Footer />
      <InsiderData
        offerCommentsCount={offer.offerCommentsCount}
        offerCoupon={offer.offerCoupon}
        offerLikes={offer.offerLikes}
        offerOldPrice={offer.offerOldPrice}
        offerPhoto={offer.offerPhoto}
        offerPrice={offer.offerPrice}
        offerTags={offer.offerTags}
        offerTitle={offer.offerTitle}
        storeName={offer.storeName}
        userName={offer.userName}
      />
    </>
  );
};

OfferPage.propTypes = {
  featuredStores: PropTypes.arrayOf(
    PropTypes.shape({
      storeName: PropTypes.string.isRequired,
      storeSlug: PropTypes.string.isRequired,
    })
  ).isRequired,
  isIndexActive: PropTypes.bool,
  // `offerKey` is either `offerId` or an `offerSlug`
  offerKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  offersToCompare: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      price: PropTypes.number.isRequired,
      redirect: PropTypes.string.isRequired,
      created: PropTypes.string.isRequired,
      store: PropTypes.shape({
        domain: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        image: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
      }),
    })
  ),
  isPromobitHasTheBestPrice: PropTypes.bool,
  relatedOffers: PropTypes.arrayOf(
    PropTypes.shape({
      aliasId: PropTypes.number,
      clicks: PropTypes.number.isRequired,
      comments: PropTypes.number.isRequired,
      coupon: PropTypes.string,
      id: PropTypes.number.isRequired,
      isHighlight: PropTypes.bool.isRequired,
      likes: PropTypes.number.isRequired,
      link: PropTypes.string.isRequired,
      oldPrice: PropTypes.number.isRequired,
      photo: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      priceType: PropTypes.string.isRequired,
      published: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      statusName: PropTypes.string.isRequired,
      store: PropTypes.shape({
        domain: PropTypes.string.isRequired,
        id: PropTypes.number,
        image: PropTypes.string,
        name: PropTypes.string.isRequired,
      }).isRequired,
      tags: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
        })
      ),
      title: PropTypes.string.isRequired,
      user: PropTypes.shape({
        id: PropTypes.number.isRequired,
        level: PropTypes.number.isRequired,
        link: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        photo: PropTypes.string.isRequired,
        since: PropTypes.string,
        specialityName: PropTypes.string,
        typeName: PropTypes.string.isRequired,
        username: PropTypes.string.isRequired,
      }).isRequired,
      views: PropTypes.number.isRequired,
    })
  ).isRequired,
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({
        id: PropTypes.number.isRequired,
        relevance: PropTypes.number.isRequired,
        text: PropTypes.string.isRequired,
        raw: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
        likes: PropTypes.number.isRequired,
        statusName: PropTypes.string.isRequired,
        stars: PropTypes.number.isRequired,
        user: PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
          username: PropTypes.string.isRequired,
          photo: PropTypes.string.isRequired,
          typeName: PropTypes.string.isRequired,
          level: PropTypes.number.isRequired,
          specialityName: PropTypes.string,
          link: PropTypes.string.isRequired,
        }).isRequired,
      }),
    })
  ).isRequired,
  reviewRate: PropTypes.number,
  serverComments: PropTypes.arrayOf(
    PropTypes.shape({
      commentId: PropTypes.number.isRequired,
      parentId: PropTypes.number,
      userId: PropTypes.number.isRequired,
      commentText: PropTypes.string.isRequired,
      commentRaw: PropTypes.string.isRequired,
      commentDate: PropTypes.string.isRequired,
      commentLikes: PropTypes.number.isRequired,
      commentStatusName: PropTypes.string.isRequired,
      commentFixed: PropTypes.bool.isRequired,
      relevance: PropTypes.number.isRequired,
      commentStars: PropTypes.number,
      reviewAliasId: PropTypes.number,
      edited: PropTypes.bool.isRequired,
      isReview: PropTypes.bool.isRequired,
      userLevel: PropTypes.number.isRequired,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userSpecialtyName: PropTypes.string,
      userTypeName: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
      offerPhoto: PropTypes.string,
      replies: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    })
  ).isRequired,
  serverOffer: PropTypes.shape({
    categoryName: PropTypes.string.isRequired,
    categorySlug: PropTypes.string.isRequired,
    hasPhysicalStore: PropTypes.bool.isRequired,
    isOfferCommentsAllowed: PropTypes.bool.isRequired,
    isStoreCnpjValid: PropTypes.bool.isRequired,
    isStoreVerified: PropTypes.bool.isRequired,
    marketplaceRate: PropTypes.bool,
    mostRelevantKeyword: PropTypes.string,
    offerClicks: PropTypes.number.isRequired,
    offerCommentsCount: PropTypes.number.isRequired,
    offerCoupon: PropTypes.string,
    offerFaq: PropTypes.array.isRequired,
    offerId: PropTypes.number.isRequired,
    offerInstructions: PropTypes.string.isRequired,
    offerInstructionsRaw: PropTypes.string.isRequired,
    offerIsHighlight: PropTypes.bool.isRequired,
    offerKeywords: PropTypes.arrayOf(PropTypes.string).isRequired,
    ratings: PropTypes.shape().isRequired,
    offerOldPrice: PropTypes.number,
    offerPhoto: PropTypes.string.isRequired,
    offerPrice: PropTypes.number.isRequired,
    offerPriceType: PropTypes.string.isRequired,
    offerPublished: PropTypes.string.isRequired,
    offerSlug: PropTypes.string.isRequired,
    offerStatusName: PropTypes.string.isRequired,
    offerUserVisibility: PropTypes.string.isRequired,
    offerTags: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      })
    ).isRequired,
    offerTitle: PropTypes.string.isRequired,
    offerType: PropTypes.string.isRequired,
    offerUserDescription: PropTypes.string,
    relatedSubcategories: PropTypes.arrayOf(
      PropTypes.shape({
        subcategoryId: PropTypes.number.isRequired,
        subcategoryName: PropTypes.string,
        subcategorySlug: PropTypes.string.isRequired,
        subcategoryWishlist: PropTypes.string.isRequired,
      })
    ).isRequired,
    reviewRate: PropTypes.number,
    storeCouponsCount: PropTypes.number.isRequired,
    storeDescription: PropTypes.string.isRequired,
    storeDomain: PropTypes.string.isRequired,
    storeImage: PropTypes.string.isRequired,
    storeMarketplace: PropTypes.shape({
      hasPhysicalStore: PropTypes.bool.isRequired,
      isMarketplaceCnpjValid: PropTypes.bool.isRequired,
      isMarketplaceVerified: PropTypes.bool.isRequired,
      storeMarketplaceName: PropTypes.string.isRequired,
    }),
    storeName: PropTypes.string.isRequired,
    storeParam: PropTypes.string.isRequired,
    subcategoryName: PropTypes.string,
    subcategorySlug: PropTypes.string.isRequired,
    userCommentsCount: PropTypes.number.isRequired,
    userCouponsCount: PropTypes.number.isRequired,
    userFirstOffer: PropTypes.bool.isRequired,
    userId: PropTypes.number.isRequired,
    userName: PropTypes.string.isRequired,
    userOffersCount: PropTypes.number.isRequired,
    userPhoto: PropTypes.string.isRequired,
    userSpecialtyName: PropTypes.string,
    userUsername: PropTypes.string.isRequired,
  }),
  sidebarAds: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      image: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      visibility: PropTypes.string.isRequired,
    })
  ),
};

export default OfferPage;
